import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Toggle } from '@/components';
import type { GateReason } from '@/permissions/permission-gates';

import { usePermissionCompare } from '../../PermissionCompareContext';
import { getPermissionMeta } from '../../permissions.ui';

import s from './PermissionRow.module.scss';

interface Props {
  name: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  /** Set when the permission is out of reach; see `GateReason`. */
  gate?: GateReason | null;
  /**
   * Set when the row is shown but must not move for this viewer — their own permissions, a
   * role at or above theirs, a permission they do not hold themselves, or one only the owner
   * group grants. Unlike a gate it says nothing about the plan, so the text stays at full
   * strength; the reason is the row's hover text.
   */
  lockedReason?: string;
  /** What a gated row says for itself: a `FeatureLock` in its `inline` variant. */
  lock?: ReactNode;
}

export default function PermissionRow({
  name,
  enabled,
  onChange,
  gate = null,
  lockedReason,
  lock,
}: Props) {
  const meta = getPermissionMeta(name);
  const rolePermissions = usePermissionCompare();
  const isOverride = rolePermissions !== undefined && enabled !== rolePermissions.has(name);
  const disabled = !!lockedReason || !!gate;

  // The hover text sits on the row: `Toggle` hands `title` to its hidden input.
  return (
    <div className={clsx(s.row, gate && s.gated)} title={lockedReason}>
      <div className={s.text}>
        <div className={s.titleRow}>
          <span className={s.title}>{meta.title}</span>
          {meta.sensitive && <span className={s.sensitive}>Sensitive</span>}
          {/* An override is a deviation from the role the person can be pulled back to;
              a gated row cannot deviate from anything, so the two never both apply. */}
          {isOverride && !gate && <span className={s.override}>Override</span>}
          {gate && lock}
        </div>
        {meta.description && <span className={s.description}>{meta.description}</span>}
      </div>
      {/* Shown even when gated, disabled and off: the server will not move it either way. */}
      <Toggle
        id={name}
        checked={!gate && enabled}
        disabled={disabled}
        className={clsx(disabled && s.gatedToggle)}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}
